/*  Greatly Social – AICaptionModal  (v2.0 – modernized UI)
    Two-step AI caption generator. All logic identical to v1.
-------------------------------------------------------------------- */
import React, { useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'

const inputStyle = {
  width:'100%', padding:'9px 12px', borderRadius:8,
  border:'1.5px solid #e5e7eb', fontSize:13, color:'#111827',
  outline:'none', boxSizing:'border-box', background:'#ffffff',
}

const labelStyle = { fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }

export default function AICaptionModal ({ show, onClose, onSave }) {
  const navigate = useNavigate()

  const [step,    setStep]    = useState('options')
  const [prompt,  setPrompt]  = useState('')
  const [caption, setCaption] = useState('')
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const [outOfCredits, setNoCredit] = useState(false)

  const [maxLength, setMaxLen]  = useState(140)
  const [tone,      setTone]    = useState('Friendly')
  const [hashOn,    setHashOn]  = useState(false)
  const [hashCount, setHashCnt] = useState(3)
  const [emojiOn,   setEmojiOn] = useState(true)
  const [showAdv,   setShowAdv] = useState(false)
  const [cta,       setCta]     = useState('none')
  const [language,  setLang]    = useState('English')
  const [persona,   setPersona] = useState('')

  const buildBody = () => ({
    prompt, maxLength, tone,
    hashtagsEnabled: hashOn, hashtagsCount: hashOn ? hashCount : 0,
    emojisEnabled: emojiOn, cta, language, brandPersona: persona,
  })

  const fetchCaption = async () => {
    if (!prompt.trim()) return
    setLoading(true); setError(''); setNoCredit(false)
    try {
      const res = await fetch('/api/ai/caption', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(buildBody()),
      })
      if (res.status === 402) { setNoCredit(true); return }
      const { caption } = await res.json()
      setCaption(caption)
      setStep('result')
    } catch (err) {
      setError(err.message || 'Generation failed')
    } finally {
      setLoading(false)
    }
  }

  const save = () => { onSave(caption); handleClose() }

  const handleClose = () => {
    onClose()
    setStep('options'); setPrompt(''); setCaption(''); setError(''); setNoCredit(false)
  }

  if (!show) return null

  return (
    <>
      {/* Backdrop */}
      <div onClick={handleClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', zIndex:1050, backdropFilter:'blur(2px)' }} />

      {/* Panel */}
      <div style={{
        position    : 'fixed',
        top         : '50%',
        left        : '50%',
        transform   : 'translate(-50%, -50%)',
        zIndex      : 1051,
        background  : '#ffffff',
        borderRadius: 16,
        width       : 560,
        maxWidth    : 'calc(100vw - 32px)',
        maxHeight   : '90vh',
        display     : 'flex',
        flexDirection:'column',
        boxShadow   : '0 20px 60px rgba(0,0,0,0.18)',
        overflow    : 'hidden',
      }}>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'18px 22px 14px', borderBottom:'1px solid #f3f4f6' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg, #7c3aed, #2563eb)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <i className='fas fa-pen-nib' style={{ fontSize:12, color:'#fff' }} />
            </div>
            <div>
              <h3 style={{ fontSize:16, fontWeight:800, color:'#111827', margin:0 }}>AI Caption Generator</h3>
              <p style={{ fontSize:12, color:'#6b7280', margin:0 }}>
                {step === 'options' ? 'Set your options and generate.' : 'Edit and save your caption.'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} style={{ background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:20 }}>×</button>
        </div>

        {/* Step indicator */}
        <div style={{ display:'flex', padding:'10px 22px', gap:6, borderBottom:'1px solid #f3f4f6' }}>
          {['options','result'].map((s, i) => (
            <div key={s} style={{ display:'flex', alignItems:'center', gap:6 }}>
              <div style={{
                width:20, height:20, borderRadius:'50%', fontSize:10, fontWeight:700,
                display:'flex', alignItems:'center', justifyContent:'center',
                background: step === s ? '#2563eb' : step === 'result' && s === 'options' ? '#dcfce7' : '#f3f4f6',
                color: step === s ? '#fff' : step === 'result' && s === 'options' ? '#16a34a' : '#9ca3af',
              }}>
                {step === 'result' && s === 'options' ? '✓' : i + 1}
              </div>
              <span style={{ fontSize:12, fontWeight:600, color: step === s ? '#2563eb' : '#9ca3af' }}>
                {s === 'options' ? 'Configure' : 'Result'}
              </span>
              {i === 0 && <i className='fas fa-chevron-right' style={{ fontSize:9, color:'#d1d5db', marginLeft:2 }} />}
            </div>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex:1, overflowY:'auto', padding:'18px 22px' }}>

          {/* Error / Credits */}
          {outOfCredits && (
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'14px 16px', marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:'#dc2626', marginBottom:10 }}>You're out of AI credits.</div>
              <button onClick={() => { handleClose(); navigate('/dashboard/credits') }} style={{ width:'100%', padding:'9px', borderRadius:8, border:'none', background:'#2563eb', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer' }}>
                Buy Credits
              </button>
            </div>
          )}
          {error && (
            <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 16px', marginBottom:16, fontSize:13, color:'#dc2626' }}>
              <i className='fas fa-exclamation-circle me-2' />{error}
            </div>
          )}

          {/* ── STEP 1: Options ── */}
          {step === 'options' && (
            <>
              <div style={{ marginBottom:16 }}>
                <label style={labelStyle}>What should this post be about?</label>
                <input type='text' style={inputStyle} placeholder="e.g. Tips for caring for succulents" value={prompt} onChange={e => setPrompt(e.target.value)}
                  onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
              </div>

              {/* Basic settings */}
              <div style={{ fontSize:12, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.07em', color:'#9ca3af', marginBottom:10 }}>Basic Settings</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:12, marginBottom:16 }}>
                <div>
                  <label style={{ ...labelStyle, fontSize:12 }}>Max length</label>
                  <input type='number' min={50} max={1000} value={maxLength} onChange={e => setMaxLen(Number(e.target.value))}
                    style={{ ...inputStyle, width:90 }}
                    onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
                </div>
                <div style={{ flex:1, minWidth:130 }}>
                  <label style={{ ...labelStyle, fontSize:12 }}>Tone</label>
                  <select value={tone} onChange={e => setTone(e.target.value)} style={{ ...inputStyle }}
                    onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'}>
                    {['Friendly','Professional','Inspirational','Funny','Snarky'].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* Toggles */}
              <div style={{ display:'flex', gap:16, marginBottom:16, flexWrap:'wrap' }}>
                {/* Hashtags */}
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:500, color:'#374151' }}>
                  <div
                    onClick={() => setHashOn(!hashOn)}
                    style={{
                      width:36, height:20, borderRadius:99, position:'relative', cursor:'pointer',
                      background: hashOn ? '#2563eb' : '#d1d5db', transition:'background 0.2s',
                    }}
                  >
                    <div style={{ position:'absolute', top:2, left: hashOn ? 18 : 2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                  Hashtags
                  {hashOn && (
                    <input type='number' min={1} max={10} value={hashCount} onChange={e => setHashCnt(Number(e.target.value))}
                      style={{ ...inputStyle, width:56, padding:'4px 8px', fontSize:12 }}
                      onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
                  )}
                </label>

                {/* Emojis */}
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:500, color:'#374151' }}>
                  <div
                    onClick={() => setEmojiOn(!emojiOn)}
                    style={{ width:36, height:20, borderRadius:99, position:'relative', cursor:'pointer', background: emojiOn ? '#2563eb' : '#d1d5db', transition:'background 0.2s' }}
                  >
                    <div style={{ position:'absolute', top:2, left: emojiOn ? 18 : 2, width:16, height:16, borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
                  </div>
                  Emojis
                </label>
              </div>

              {/* Advanced toggle */}
              <button
                type='button'
                onClick={() => setShowAdv(p => !p)}
                style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:12, fontWeight:600, color:'#6b7280', padding:'0 0 12px', display:'flex', alignItems:'center', gap:5 }}
              >
                <i className={`fas fa-chevron-${showAdv ? 'up' : 'down'}`} style={{ fontSize:10 }} />
                {showAdv ? 'Hide advanced' : 'Show advanced options'}
              </button>

              {showAdv && (
                <div style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:10, padding:'14px 16px', display:'flex', flexDirection:'column', gap:12 }}>
                  <div>
                    <label style={{ ...labelStyle, fontSize:12 }}>Call-to-Action strength</label>
                    <select value={cta} onChange={e => setCta(e.target.value)} style={inputStyle}
                      onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'}>
                      <option value='none'>None</option>
                      <option value='soft'>Soft</option>
                      <option value='strong'>Strong</option>
                    </select>
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize:12 }}>Language</label>
                    <select value={language} onChange={e => setLang(e.target.value)} style={inputStyle}
                      onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'}>
                      {['English','Spanish','French','German','Italian','Portuguese','Dutch'].map(l => <option key={l}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{ ...labelStyle, fontSize:12 }}>Brand persona / voice <span style={{ color:'#9ca3af', fontWeight:400 }}>(optional)</span></label>
                    <textarea rows={2} value={persona} onChange={e => setPersona(e.target.value)} placeholder='Optional style guide or voice description'
                      style={{ ...inputStyle, resize:'vertical' }}
                      onFocus={e => e.target.style.borderColor='#2563eb'} onBlur={e => e.target.style.borderColor='#e5e7eb'} />
                  </div>
                </div>
              )}
            </>
          )}

          {/* ── STEP 2: Result ── */}
          {step === 'result' && (
            <>
              <label style={labelStyle}>Generated caption</label>
              <textarea
                rows={5}
                value={caption}
                onChange={e => setCaption(e.target.value)}
                style={{ ...inputStyle, resize:'vertical', lineHeight:1.6 }}
                onFocus={e => e.target.style.borderColor='#2563eb'}
                onBlur={e => e.target.style.borderColor='#e5e7eb'}
              />
              <p style={{ fontSize:12, color:'#9ca3af', marginTop:6 }}>You can edit the caption above before saving.</p>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding:'14px 22px', borderTop:'1px solid #f3f4f6', display:'flex', justifyContent:'flex-end', gap:8 }}>
          {step === 'options' && (
            <>
              <button onClick={handleClose} style={{ padding:'8px 16px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'transparent', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                Cancel
              </button>
              <button
                onClick={fetchCaption}
                disabled={loading || !prompt.trim()}
                style={{
                  padding:'8px 20px', borderRadius:8, border:'none',
                  background: (loading || !prompt.trim()) ? '#e5e7eb' : 'linear-gradient(135deg, #7c3aed, #2563eb)',
                  color: (loading || !prompt.trim()) ? '#9ca3af' : '#fff',
                  fontSize:13, fontWeight:700, cursor: (loading || !prompt.trim()) ? 'not-allowed' : 'pointer',
                  display:'flex', alignItems:'center', gap:7,
                }}
              >
                {loading
                  ? <><Spinner animation='border' size='sm' style={{ width:13, height:13 }} /> Generating…</>
                  : <><i className='fas fa-magic' style={{ fontSize:11 }} /> Generate</>
                }
              </button>
            </>
          )}

          {step === 'result' && (
            <>
              <button onClick={() => setStep('options')} style={{ padding:'8px 14px', borderRadius:8, border:'1.5px solid #e5e7eb', background:'transparent', color:'#374151', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                <i className='fas fa-arrow-left me-1' style={{ fontSize:10 }} /> Back
              </button>
              <button
                onClick={fetchCaption}
                disabled={loading}
                style={{
                  padding:'8px 14px', borderRadius:8, border:'1.5px solid #e5e7eb',
                  background:'transparent', color:'#374151', fontSize:13, fontWeight:600,
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display:'flex', alignItems:'center', gap:6,
                }}
              >
                {loading
                  ? <><Spinner animation='border' size='sm' style={{ width:12, height:12 }} /> Regenerating…</>
                  : <><i className='fas fa-redo' style={{ fontSize:10 }} /> Regenerate</>
                }
              </button>
              <button
                onClick={save}
                style={{ padding:'8px 18px', borderRadius:8, border:'none', background:'#16a34a', color:'#fff', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}
              >
                <i className='fas fa-check' style={{ fontSize:11 }} /> Save Caption
              </button>
            </>
          )}
        </div>
      </div>
    </>
  )
}